import { useState, useEffect, useCallback, useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Search, Phone, ChevronDown, ChevronUp, Download, Filter, Loader2, ShieldCheck, ArrowUpDown } from "lucide-react";
import { chatbotService, type ChatSessionSummaryDto } from "@/services/chatbotService";
import { teamService, type SuperAdminTeamOption } from "@/services/teamService";
import { SOPHIE_TEAM_OVERRIDE_STORAGE_KEY } from "@/lib/http";
import { toast } from "sonner";
import SendFirstInvoiceCard from "./SendFirstInvoiceCard";

interface CallRecord {
  id: string;
  teamId?: string;
  teamName?: string;
  caller: string;
  phone: string;
  createdAt: number;
  timestamp: string;
  summary: string;
  channel: "Phone" | "SMS";
  sentiment: string;
  duration: string;
  durationSeconds: number;
  transcript: string;
  fullSummary: string;
  reason: string;
  actions: string[];
  successfull?: boolean;
  bookingDone?: boolean;
  sttProvider?: string;
  sttLanguage?: string;
  messages?: Array<{ role: string; content: string; timestamp?: string; order?: number; source?: string; isVoice?: boolean }>;
}

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const parseActions = (action?: string): string[] => {
  if (!action) return [];
  try {
    const parsed = JSON.parse(action);
    if (Array.isArray(parsed)) {
      return parsed.map(String);
    }
    return [String(parsed)];
  } catch {
    return [action];
  }
};

const NO_TEAM_OVERRIDE_VALUE = "__none__";

const getStoredTeamOverride = (): string => {
  try {
    const stored = sessionStorage.getItem(SOPHIE_TEAM_OVERRIDE_STORAGE_KEY)?.trim();
    return stored || NO_TEAM_OVERRIDE_VALUE;
  } catch {
    return NO_TEAM_OVERRIDE_VALUE;
  }
};

const mapSessionToCallRecord = (session: ChatSessionSummaryDto): CallRecord => {
  return {
    id: session.id,
    teamId: session.teamId,
    teamName: session.teamName,
    caller: session.caller || "Unknown Caller",
    phone: session.userId || "Unknown",
    createdAt: new Date(session.createdAt).getTime(),
    timestamp: new Date(session.createdAt).toLocaleString(),
    summary: session.summary || "No summary available",
    channel: "Phone",
    sentiment: session.sentiment || "Neutral",
    duration: formatDuration(session.duration || 0),
    durationSeconds: session.duration || 0,
    transcript: "",
    fullSummary: session.summary || "",
    reason: session.reason || "General Inquiry",
    actions: parseActions(session.action),
    successfull: session.successfull,
    bookingDone: session.bookingDone,
    sttProvider: session.sttProvider,
    sttLanguage: session.sttLanguage,
    messages: [],
  };
};

export default function SuperAdmin() {
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [teamOptions, setTeamOptions] = useState<SuperAdminTeamOption[]>([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [selectedTeamOverrideId, setSelectedTeamOverrideId] = useState<string>(getStoredTeamOverride);
  const [searchQuery, setSearchQuery] = useState("");
  const [sentimentFilter, setSentimentFilter] = useState<string>("all");
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: "asc" | "desc" }>({
    key: "timestamp",
    direction: "desc",
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({});

  const selectedTeamName = useMemo(
    () => teamOptions.find((team) => team.id === selectedTeamOverrideId)?.name,
    [teamOptions, selectedTeamOverrideId]
  );

  const loadTeams = useCallback(async () => {
    try {
      setIsLoadingTeams(true);
      const teams = await teamService.getSophieTeamsForSuperAdmin();
      setTeamOptions(teams);
      setSelectedTeamOverrideId((current) => {
        if (current === NO_TEAM_OVERRIDE_VALUE || teams.some((team) => team.id === current)) {
          return current;
        }
        try {
          sessionStorage.removeItem(SOPHIE_TEAM_OVERRIDE_STORAGE_KEY);
        } catch {
          // ignore storage errors
        }
        return NO_TEAM_OVERRIDE_VALUE;
      });
    } catch (error) {
      console.error("Failed to load teams:", error);
      toast.error("Failed to load teams");
    } finally {
      setIsLoadingTeams(false);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      const sessions =
        selectedTeamOverrideId === NO_TEAM_OVERRIDE_VALUE
          ? await chatbotService.listAllSessions(500)
          : await chatbotService.listSessions({ limit: 500 });
      const mappedCalls: CallRecord[] = sessions.map(mapSessionToCallRecord);
      setCalls(mappedCalls);
    } catch (error) {
      console.error("Failed to load sessions:", error);
      toast.error("Failed to load chat history");
    } finally {
      setIsLoading(false);
    }
  }, [selectedTeamOverrideId]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleTeamOverrideChange = (teamId: string) => {
    setExpandedId(null);
    setLoadingDetails({});
    setCalls([]);
    setSearchQuery("");
    if (teamId === NO_TEAM_OVERRIDE_VALUE) {
      setSelectedTeamOverrideId(NO_TEAM_OVERRIDE_VALUE);
      try {
        sessionStorage.removeItem(SOPHIE_TEAM_OVERRIDE_STORAGE_KEY);
      } catch {
        // ignore storage errors
      }
      toast.success("Switched back to default team context");
      return;
    }

    setSelectedTeamOverrideId(teamId);
    try {
      sessionStorage.setItem(SOPHIE_TEAM_OVERRIDE_STORAGE_KEY, teamId);
    } catch {
      // ignore storage errors
    }
    const teamName = teamOptions.find((team) => team.id === teamId)?.name;
    toast.success(teamName ? `Now acting as ${teamName}` : "Team context updated");
  };

  const handleExpand = async (id: string, isOpen: boolean) => {
    setExpandedId(isOpen ? id : null);

    if (isOpen) {
      const call = calls.find((c) => c.id === id);
      if (call && (!call.messages || call.messages.length === 0) && !call.transcript) {
        await loadSessionDetails(id);
      }
    }
  };

  const loadSessionDetails = async (id: string) => {
    setLoadingDetails((prev) => ({ ...prev, [id]: true }));
    try {
      const details = await chatbotService.getSession(id);
      setCalls((prevCalls) =>
        prevCalls.map((call) => {
          if (call.id === id) {
            const transcript = details.messages
              ? details.messages.map((m) => `${m.role}: ${m.content}`).join("\n\n")
              : "No transcript available";

            return {
              ...call,
              transcript,
              messages: details.messages,
              summary: details.summary || call.summary,
              fullSummary: details.summary || call.fullSummary,
              sentiment: details.sentiment || call.sentiment,
              reason: details.reason || call.reason,
              actions: details.action ? parseActions(details.action) : call.actions,
              successfull: typeof details.successfull === "boolean" ? details.successfull : call.successfull,
              bookingDone: typeof details.bookingDone === "boolean" ? details.bookingDone : call.bookingDone,
              sttProvider: details.sttProvider ?? call.sttProvider,
              sttLanguage: details.sttLanguage ?? call.sttLanguage,
              teamId: details.teamId ?? call.teamId,
              teamName: details.teamName ?? call.teamName,
            };
          }
          return call;
        })
      );
    } catch (error) {
      console.error(`Failed to load details for session ${id}:`, error);
      toast.error("Failed to load call details");
    } finally {
      setLoadingDetails((prev) => ({ ...prev, [id]: false }));
    }
  };

  const getSentimentColor = (sentiment: string) => {
    const normalized = sentiment.toLowerCase();
    if (normalized.includes("positive")) return "bg-status-success/10 text-status-success border-status-success/20";
    if (normalized.includes("neutral")) return "bg-status-warning/10 text-status-warning border-status-warning/20";
    if (normalized.includes("negative")) return "bg-status-pending/10 text-status-pending border-status-pending/20";
    if (normalized.includes("frustrated") || normalized.includes("angry"))
      return "bg-status-error/10 text-status-error border-status-error/20";

    return "bg-secondary text-secondary-foreground";
  };

  type SortKey =
    | "caller"
    | "team"
    | "timestamp"
    | "duration"
    | "summary"
    | "sentiment"
    | "successfull"
    | "bookingDone"
    | "sttProvider"
    | "sttLanguage";

  const filteredCalls = calls.filter((call) => {
    const matchesSearch =
      call.caller.toLowerCase().includes(searchQuery.toLowerCase()) ||
      call.phone.includes(searchQuery) ||
      call.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (call.teamName ?? call.teamId ?? "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSentiment =
      sentimentFilter === "all" || (call.sentiment && call.sentiment.toLowerCase() === sentimentFilter.toLowerCase());
    return matchesSearch && matchesSentiment;
  });

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedCalls = useMemo(() => {
    const getSortValue = (call: CallRecord, key: SortKey): string | number => {
      switch (key) {
        case "caller":
          return call.caller;
        case "team":
          return call.teamName ?? call.teamId ?? "";
        case "timestamp":
          return call.createdAt;
        case "duration":
          return call.durationSeconds;
        case "summary":
          return call.summary;
        case "sentiment":
          return call.sentiment;
        case "successfull":
          return call.successfull ? 1 : 0;
        case "bookingDone":
          return call.bookingDone ? 1 : 0;
        case "sttProvider":
          return call.sttProvider ?? "";
        case "sttLanguage":
          return call.sttLanguage ?? "";
        default:
          return "";
      }
    };

    const direction = sortConfig.direction === "asc" ? 1 : -1;
    return [...filteredCalls].sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);

      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * direction;
      }

      return String(aValue).localeCompare(String(bValue), undefined, { numeric: true }) * direction;
    });
  }, [filteredCalls, sortConfig]);

  const getSortIcon = (key: SortKey) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-3 h-3 text-muted-foreground" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="w-3 h-3 text-foreground" />
    ) : (
      <ChevronDown className="w-3 h-3 text-foreground" />
    );
  };

  const getBoolBadge = (value?: boolean) =>
    value ? (
      <Badge variant="outline" className="bg-status-success/10 text-status-success border-status-success/40">
        Yes
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-muted text-muted-foreground border-border">No</Badge>
    );

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Super Admin - All Chats
          </h2>
          <p className="text-muted-foreground mt-1">
            {selectedTeamOverrideId === NO_TEAM_OVERRIDE_VALUE
              ? "View every chat session across all teams."
              : `Currently acting as ${selectedTeamName || "selected team"} for this browser session.`}
          </p>
        </div>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <Card className="p-4 card-hover">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div>
            <p className="text-sm font-medium">Switch Team Context</p>
            <p className="text-sm text-muted-foreground">
              Pick a Sophie team to temporarily act as that team in this browser session.
            </p>
          </div>
          <Select value={selectedTeamOverrideId} onValueChange={handleTeamOverrideChange} disabled={isLoadingTeams}>
            <SelectTrigger className="w-full lg:w-[340px]">
              <SelectValue placeholder={isLoadingTeams ? "Loading teams..." : "Select a team"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_TEAM_OVERRIDE_VALUE}>Default Team (No Override)</SelectItem>
              {teamOptions.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name || team.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-4 card-hover">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by caller name, phone, summary, or team id..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
            <SelectTrigger className="w-full md:w-[200px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by sentiment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sentiments</SelectItem>
              <SelectItem value="Positive">Positive</SelectItem>
              <SelectItem value="Neutral">Neutral</SelectItem>
              <SelectItem value="Negative">Negative</SelectItem>
              <SelectItem value="Frustrated">Frustrated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {isLoading ? (
        <Card className="p-12 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading chat history...</p>
        </Card>
      ) : (
        <>
          <div className="hidden lg:block">
            <Card className="card-hover overflow-hidden">
              <div className="overflow-auto max-h-[70vh]">
                <table className="w-full min-w-[1200px]">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold">
                        <button type="button" onClick={() => handleSort("caller")} className="flex items-center gap-1">
                          Caller
                          {getSortIcon("caller")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button type="button" onClick={() => handleSort("team")} className="flex items-center gap-1">
                          Team
                          {getSortIcon("team")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button
                          type="button"
                          onClick={() => handleSort("timestamp")}
                          className="flex items-center gap-1"
                        >
                          Timestamp
                          {getSortIcon("timestamp")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button type="button" onClick={() => handleSort("duration")} className="flex items-center gap-1">
                          Duration
                          {getSortIcon("duration")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button type="button" onClick={() => handleSort("summary")} className="flex items-center gap-1">
                          Summary
                          {getSortIcon("summary")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button
                          type="button"
                          onClick={() => handleSort("sentiment")}
                          className="flex items-center gap-1"
                        >
                          Sentiment
                          {getSortIcon("sentiment")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button
                          type="button"
                          onClick={() => handleSort("successfull")}
                          className="flex items-center gap-1"
                        >
                          Successful
                          {getSortIcon("successfull")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button
                          type="button"
                          onClick={() => handleSort("bookingDone")}
                          className="flex items-center gap-1"
                        >
                          BookingDone
                          {getSortIcon("bookingDone")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button
                          type="button"
                          onClick={() => handleSort("sttProvider")}
                          className="flex items-center gap-1"
                        >
                          SttProvider
                          {getSortIcon("sttProvider")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold">
                        <button
                          type="button"
                          onClick={() => handleSort("sttLanguage")}
                          className="flex items-center gap-1"
                        >
                          SttLanguage
                          {getSortIcon("sttLanguage")}
                        </button>
                      </th>
                      <th className="text-left p-4 font-semibold w-[50px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCalls.map((call) => (
                      <Collapsible
                        key={call.id}
                        open={expandedId === call.id}
                        onOpenChange={(open) => handleExpand(call.id, open)}
                        asChild
                      >
                        <>
                          <CollapsibleTrigger asChild>
                            <tr className="border-b hover:bg-accent/50 cursor-pointer transition-colors">
                              <td className="p-4">
                                <div>
                                  <p className="font-medium">{call.caller}</p>
                                  <p className="text-sm text-muted-foreground">{call.phone}</p>
                                </div>
                              </td>
                              <td className="p-4 text-sm text-muted-foreground">{call.teamName || call.teamId || "—"}</td>
                              <td className="p-4">
                                <div className="text-sm">
                                  <p>{call.timestamp.split(",")[0]}</p>
                                  <p className="text-muted-foreground">{call.timestamp.split(",")[1]}</p>
                                </div>
                              </td>
                              <td className="p-4">
                                <p className="text-sm font-medium">{call.duration}</p>
                              </td>
                              <td className="p-4">
                                <p className="text-sm line-clamp-2">{call.summary}</p>
                              </td>
                              <td className="p-4">
                                <Badge className={getSentimentColor(call.sentiment)}>{call.sentiment}</Badge>
                              </td>
                              <td className="p-4">{getBoolBadge(call.successfull)}</td>
                              <td className="p-4">{getBoolBadge(call.bookingDone)}</td>
                              <td className="p-4 text-sm text-muted-foreground">{call.sttProvider || "—"}</td>
                              <td className="p-4 text-sm text-muted-foreground">{call.sttLanguage || "—"}</td>
                              <td className="p-4">
                                {expandedId === call.id ? (
                                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                )}
                              </td>
                            </tr>
                          </CollapsibleTrigger>
                          <CollapsibleContent asChild>
                            <tr>
                              <td colSpan={11} className="p-0">
                                <div className="bg-muted/30 p-6 space-y-6">
                                  {loadingDetails[call.id] ? (
                                    <div className="flex justify-center p-4">
                                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                    </div>
                                  ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      <div>
                                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                                          Full Transcript
                                          <Badge variant="secondary" className="text-xs">
                                            {call.duration}
                                          </Badge>
                                        </h4>
                                        <div className="bg-background rounded-lg p-4 max-h-[400px] overflow-y-auto">
                                          <pre className="text-sm whitespace-pre-wrap font-sans">
                                            {call.transcript || "No transcript available."}
                                          </pre>
                                        </div>
                                      </div>

                                      <div className="space-y-4">
                                        <div>
                                          <h4 className="font-semibold mb-2">Call Summary</h4>
                                          <p className="text-sm text-muted-foreground bg-background rounded-lg p-4">
                                            {call.fullSummary || "No summary available."}
                                          </p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                          <div>
                                            <p className="text-muted-foreground">Reason</p>
                                            <Badge variant="secondary" className="text-xs">
                                              {call.reason}
                                            </Badge>
                                          </div>
                                          <div>
                                            <p className="text-muted-foreground">Team</p>
                                            <p className="font-medium">{call.teamName || call.teamId || "—"}</p>
                                          </div>
                                          <div>
                                            <p className="text-muted-foreground">Successful</p>
                                            {getBoolBadge(call.successfull)}
                                          </div>
                                          <div>
                                            <p className="text-muted-foreground">Booking Done</p>
                                            {getBoolBadge(call.bookingDone)}
                                          </div>
                                          <div>
                                            <p className="text-muted-foreground">STT Provider</p>
                                            <p className="font-medium">{call.sttProvider || "—"}</p>
                                          </div>
                                          <div>
                                            <p className="text-muted-foreground">STT Language</p>
                                            <p className="font-medium">{call.sttLanguage || "—"}</p>
                                          </div>
                                        </div>

                                        <div>
                                          <h4 className="font-semibold mb-2">Actions Taken</h4>
                                          {call.actions.length > 0 ? (
                                            <ul className="space-y-2">
                                              {call.actions.map((action, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-sm">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                                  <span>{action}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          ) : (
                                            <p className="text-sm text-muted-foreground">No actions recorded.</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </CollapsibleContent>
                        </>
                      </Collapsible>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Mobile view */}
          <div className="lg:hidden space-y-4">
            {sortedCalls.map((call) => (
              <Collapsible
                key={call.id}
                open={expandedId === call.id}
                onOpenChange={(open) => handleExpand(call.id, open)}
              >
                <Card className="card-hover">
                  <CollapsibleTrigger asChild>
                    <div className="p-4 cursor-pointer">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="font-semibold">{call.caller}</p>
                          <p className="text-sm text-muted-foreground">{call.phone}</p>
                          <p className="text-xs text-muted-foreground mt-1">Team: {call.teamName || call.teamId || "—"}</p>
                        </div>
                        {expandedId === call.id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <Badge className={getSentimentColor(call.sentiment)}>{call.sentiment}</Badge>
                        <span className="text-xs text-muted-foreground">{call.timestamp}</span>
                      </div>

                      <div className="flex gap-2 text-xs mb-2">
                        {getBoolBadge(call.successfull)}
                        {getBoolBadge(call.bookingDone)}
                      </div>

                      <p className="text-sm text-muted-foreground line-clamp-2">{call.summary}</p>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t p-4 bg-muted/30 space-y-4">
                      {loadingDetails[call.id] ? (
                        <div className="flex justify-center p-4">
                          <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        </div>
                      ) : (
                        <>
                          <div>
                            <h4 className="font-semibold mb-2 text-sm">Full Transcript</h4>
                            <div className="bg-background rounded-lg p-3 max-h-[300px] overflow-y-auto">
                              <pre className="text-xs whitespace-pre-wrap font-sans">
                                {call.transcript || "No transcript available."}
                              </pre>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div>
                              <p className="text-muted-foreground">Reason</p>
                              <p className="font-medium">{call.reason}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">STT Provider</p>
                              <p className="font-medium">{call.sttProvider || "—"}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">STT Language</p>
                              <p className="font-medium">{call.sttLanguage || "—"}</p>
                            </div>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 text-sm">Call Summary</h4>
                            <p className="text-sm text-muted-foreground">{call.fullSummary}</p>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 text-sm">Actions Taken</h4>
                            {call.actions.length > 0 ? (
                              <ul className="space-y-1.5">
                                {call.actions.map((action, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                    <span>{action}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-muted-foreground">No actions recorded.</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>

          {filteredCalls.length === 0 && (
            <Card className="p-12 text-center">
              <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-20" />
              <p className="text-muted-foreground">No calls found matching your filters</p>
            </Card>
          )}
        </>
      )}

      <SendFirstInvoiceCard />
    </div>
  );
}
